import baseStyles from './index.css?raw';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LearningPage, MathEducationProvider, useMathEducation } from './components/MathEducationProvider';
// import { ResetButton } from './components/ResetButton';
import { BlockBuilder } from './components/blocks';
import { AdvancedModeRatioNew } from './components/AdvancedModeRatioNew';
import ProblemTreePanel from './components/ProblemTreePanel';
import AdvancedModeProgressPanel from './components/AdvancedModeProgressPanel';
import SectionSidebar, { SectionSidebarItem } from './components/SectionSidebar';
import { CONNECT_SLIDES, ConnectInnerNav, ConnectSlideId, ConnectSlides } from './components/ConnectSlides';
import { AssessmentComponent } from '../../../components/AssessmentComponent/AssessmentComponent';
import { useLrnSessionBySkill } from '../../../custom-hooks/useLrnSession';
import { useStudentSkillStates } from '../../../custom-hooks/useStudentSkillStates';
import {
  isAdvancedModeUnlocked,
  ACHIEVEMENT_DISPLAY_ORDER,
  getAchievementMetadata,
} from './components/achievements/achievementMetadata';
import { TooltipProvider } from './components/ui/tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from './components/ui/tooltip';
import { RotateCcw } from 'lucide-react';
import { BetaLabel } from './components/BetaLabel';
import { BETA_FEEDBACK_OBSERVATION_VALUE_BY_RATING, BetaFeedbackRating } from './components/betaFeedback';
import {
  CHALLENGE_PASSING_SCORE,
  SKILL_213_CODE,
  TEAME_183_FLAG_ID,
  DEFAULT_SKILL_213_LOGGING_DATA,
  TEAME_183_MAIN_VIEWPORT_MIN_HEIGHT_PX,
  TEAME_183_MAIN_VIEWPORT_MIN_WIDTH_PX,
  TEAME_183_SIDEBAR_MAX_WIDTH_PX,
  TEAME_183_SIDEBAR_MIN_WIDTH_PX,
  TEAME_183_SIDEBAR_WIDTH_PERCENT,
} from '../constants';
import type { Skill213LoggingData } from '../constants';
import { useCondition } from '../../ExperimentsProvider';
import { useExperimentSpeculativeData } from '../../../hooks/useExperimentSpeculativeData';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { recordExperimentObservation } from '../../../graphQLOperations/Experiments';
import { currentStudentSkillQuery } from '../../../graphQLOperations/SingleSkill';
import { SingleSkillQuery } from '../../../graphQLOperations/__generated__/SingleSkillQuery.graphql';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { PreviousAndNextSSS } from '../../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { AttemptInformation } from '../../../models/AttemptInformation';
import { PreviousAttempts } from '../../../models/PreviousAttempts';
// import { SkillResourceSteps } from '../../../models/Timeline';
import { RiceCookingGame } from './components/RiceCookingGame';
import { isAllPerfectFound } from './components/riceGameLogic';
import { ChallengeInnerNav } from './components/ChallengeInnerNav';
import { FullSizeViewport } from './components/layout/FullSizeViewport';
import {
  ExperimentsObservationInput,
  ExperimentsRecordExperimentsObservationMutation,
} from '../../../graphQLOperations/__generated__/ExperimentsRecordExperimentsObservationMutation.graphql';

function AppContent() {
  const navigate = useNavigate();
  const {
    progress,
    activateAdvancedMode,
    setCurrentPage,
    setSelectedGoal,
    updateProgress,
    unlockingBadges,
    onBadgeUnlockComplete,
  } = useMathEducation();
  const userRedux = useAppSelector(selectUser);
  const { studentSkillStates } = useStudentSkillStates({ includeExtensions: true });
  const skill213State = studentSkillStates.find(s => s.skill.code === SKILL_213_CODE);

  const [commitObservationMutation] =
    useMutation<ExperimentsRecordExperimentsObservationMutation>(recordExperimentObservation);
  const teame183Variant = useCondition(TEAME_183_FLAG_ID);
  const {
    data: teame183LoggingData,
    setData: setTeame183LoggingData,
    hydrated: teame183LoggingHydrated,
  } = useExperimentSpeculativeData<Skill213LoggingData>({
    publicUserId: userRedux?.sub,
    userNodeId: userRedux?.id ? `UserNode:${userRedux.id}` : undefined,
    flagName: TEAME_183_FLAG_ID,
    variantName: teame183Variant,
    defaultValues: DEFAULT_SKILL_213_LOGGING_DATA,
  });
  const hasLoggedAccessRef = useRef(false);
  const lastLoggedAttemptRef = useRef<AttemptInformation | null>(null);
  const [attemptInformation, setAttemptInformation] = useState<AttemptInformation | null>(null);
  const [previousAttempts, setPreviousAttempts] = useState<PreviousAttempts[]>([]);
  const skillId = skill213State?.skill?.id || '';
  const skillName = skill213State?.skill?.name || 'Skill Challenge';

  const { skillAssessmentsByStudentId } = useLazyLoadQuery<SingleSkillQuery>(
    currentStudentSkillQuery,
    {
      studentId: `StudentNode:${userRedux?.id}`,
      filter: { skillId },
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (skillAssessmentsByStudentId?.nodes) {
      setPreviousAttempts(skillAssessmentsByStudentId.nodes.filter(Boolean) as PreviousAttempts[]);
    }
  }, [skillAssessmentsByStudentId]);

  const {
    data: lrnSessionData,
    refetchLrnSession,
    // loadPreviousSesssion,
    setAssessmentState,
  } = useLrnSessionBySkill(skillId, !skillId);

  const sssAfterTakeAssessment: PreviousAndNextSSS = {
    previousState: skill213State ?? null,
    nextState: null,
  };

  const handleBetaFeedbackTextChange = useCallback(
    (nextFeedbackText: string) => {
      updateProgress({
        betaFeedbackText: nextFeedbackText,
      });
    },
    [updateProgress],
  );

  const handleBetaRatingChange = useCallback(
    (nextRating: BetaFeedbackRating) => {
      if (progress.betaFeedbackRating === nextRating) {
        return;
      }

      updateProgress({
        betaFeedbackRating: nextRating,
      });

      if (!userRedux?.id) {
        return;
      }

      commitObservationMutation({
        variables: {
          input: {
            input: {
              flagName: TEAME_183_FLAG_ID,
              variantName: nextRating,
              observationType: 'approval-vote',
              observation: BETA_FEEDBACK_OBSERVATION_VALUE_BY_RATING[nextRating],
            },
          },
        },
        onCompleted: (data: unknown) => {
          const response = data as {
            addExperimentsObservations?: {
              errors?: Array<{ __typename: string; message?: string }>;
            };
          };
          const hasErrors = Boolean(response.addExperimentsObservations?.errors?.length);
          if (hasErrors) {
            console.warn(
              'Failed to record TEAME-183 approval vote observation',
              response.addExperimentsObservations?.errors,
            );
          }
        },
        onError: error => {
          console.warn('Failed to record TEAME-183 approval vote observation', error);
        },
      });
    },
    [commitObservationMutation, progress.betaFeedbackRating, updateProgress, userRedux?.id],
  );

  useEffect(() => {
    const styleId = 'teame-183-base-styles';
    let addedByThisMount = false;

    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = baseStyles;
      document.head.appendChild(styleElement);
      addedByThisMount = true;
    }

    return () => {
      if (addedByThisMount) {
        document.getElementById(styleId)?.remove();
      }
    };
  }, []);

  // TEAME-183: Log skill_213_access observation and record first access timestamp
  useEffect(() => {
    if (!userRedux?.id || !teame183LoggingHydrated || hasLoggedAccessRef.current) {
      return;
    }

    hasLoggedAccessRef.current = true;

    if (teame183LoggingData.firstAccessTimestamp === null) {
      setTeame183LoggingData(prev => ({ ...prev, firstAccessTimestamp: Date.now() }));
    }

    const experimentObservation: ExperimentsObservationInput = {
      flagName: TEAME_183_FLAG_ID,
      variantName: teame183Variant,
      observationType: 'skill_213_access',
      observation: 1,
    };

    commitObservationMutation({
      variables: {
        input: {
          input: experimentObservation,
        },
      },
      onError: error => {
        console.error('Failed to log skill_213_access observation:', error);
      },
    });

    const hasPreviouslyPassed = previousAttempts.some(a => a.score >= CHALLENGE_PASSING_SCORE);
    experimentObservation.observationType = 'skill-213-passed-previously';
    experimentObservation.observation = hasPreviouslyPassed ? 1 : 0;

    commitObservationMutation({
      variables: {
        input: {
          input: experimentObservation,
        },
      },
      onError: error => {
        console.error('Failed to log skill-213-passed-previously observation:', error);
      },
    });
  }, [
    userRedux?.id,
    teame183LoggingHydrated,
    commitObservationMutation,
    teame183Variant,
    teame183LoggingData.firstAccessTimestamp,
    setTeame183LoggingData,
    previousAttempts,
  ]);

  // TEAME-183: Log pass/fail and minutes-to-pass when a challenge attempt completes
  useEffect(() => {
    if (!attemptInformation?.totalScore || !userRedux?.id) {
      return;
    }

    if (lastLoggedAttemptRef.current === attemptInformation) {
      return;
    }
    lastLoggedAttemptRef.current = attemptInformation;

    const hasPassed = attemptInformation.totalScore >= CHALLENGE_PASSING_SCORE;

    commitObservationMutation({
      variables: {
        input: {
          input: {
            flagName: TEAME_183_FLAG_ID,
            variantName: teame183Variant,
            observationType: 'pass_fail',
            observation: hasPassed ? 1 : 0,
          },
        },
      },
      onError: error => {
        console.error('Failed to log TEAME-183 pass/fail observation:', error);
      },
    });

    if (hasPassed && teame183LoggingData.firstAccessTimestamp !== null && teame183LoggingData.minutesToPass === null) {
      const minutesToPass = (Date.now() - teame183LoggingData.firstAccessTimestamp) / 60000;
      commitObservationMutation({
        variables: {
          input: {
            input: {
              flagName: TEAME_183_FLAG_ID,
              variantName: teame183Variant,
              observationType: 'minutes-to-skill-213-pass',
              observation: minutesToPass,
            },
          },
        },
        onError: error => {
          console.error('Failed to log minutes-to-skill-213-pass observation:', error);
        },
      });
      setTeame183LoggingData(prev => ({ ...prev, minutesToPass }));
    }
  }, [
    attemptInformation,
    userRedux?.id,
    commitObservationMutation,
    teame183Variant,
    teame183LoggingData.firstAccessTimestamp,
    teame183LoggingData.minutesToPass,
    setTeame183LoggingData,
  ]);

  // Check if advanced mode should be unlocked
  // This uses isAdvancedModeUnlocked() which checks the prerequisites for 'advanced-button'
  // defined in achievementMetadata.ts. This is the SINGLE SOURCE OF TRUTH for advanced mode unlock.
  // To change what unlocks advanced mode, update the prerequisites in achievementMetadata.ts
  const advancedModeUnlocked = isAdvancedModeUnlocked(progress.achievements);
  const connectViewedSlideIds = progress.connectViewedSlideIds as ConnectSlideId[];
  const isConnectSectionComplete = CONNECT_SLIDES.every(slide => connectViewedSlideIds.includes(slide.id));
  const isExploreSectionComplete = advancedModeUnlocked;
  const isPracticeMenuUnlocked = isExploreSectionComplete || isConnectSectionComplete;
  const isPracticePage = progress.currentPage === 'practice';
  const isChallengePage = progress.currentPage === 'challenge';
  const connectCurrentSlideIndex = Math.min(Math.max(progress.connectCurrentSlideIndex, 0), CONNECT_SLIDES.length - 1);

  const handleAdvancedModeClick = () => {
    activateAdvancedMode();
  };

  const handleSidebarSectionSelect = useCallback(
    (sectionId: LearningPage) => {
      if (sectionId === 'practice') {
        activateAdvancedMode();
        return;
      }
      setCurrentPage(sectionId);
    },
    [activateAdvancedMode, setCurrentPage],
  );

  const handleResetDiagram = () => {
    // Increment manipulativeResetKey to force BlockBuilder to remount with initial state
    // (Value A: 3 blocks, Value B: 2 blocks)
    // This does NOT increment resetKey, so it won't trigger goal selection changes
    updateProgress({
      manipulativeResetKey: Date.now(),
      manipulativeState: {
        lines: [],
        arrangement: 'parallel',
        cloneMultiplier: 1,
      },
    });
  };

  const handleSkillChallengeClick = () => {
    setCurrentPage('challenge');
  };

  const handleConnectSlideSelect = useCallback(
    (index: number) => {
      const boundedIndex = Math.min(Math.max(index, 0), CONNECT_SLIDES.length - 1);
      updateProgress({
        connectCurrentSlideIndex: boundedIndex,
      });
    },
    [updateProgress],
  );

  const handleConnectComplete = useCallback(() => {
    if (isPracticeMenuUnlocked) {
      activateAdvancedMode();
      return;
    }
    setCurrentPage('explore');
  }, [activateAdvancedMode, isPracticeMenuUnlocked, setCurrentPage]);

  // Define problem progress rows (always fully filled)
  const rows = [
    { label: 'Problem 1', color: 'tw-bg-blue-500', glow: '#3b82f6', maxCount: 5, count: 5 },
    { label: 'Problem 2', color: 'tw-bg-green-500', glow: '#22c55e', maxCount: 5, count: 5 },
    { label: 'Problem 3', color: 'tw-bg-purple-500', glow: '#a855f7', maxCount: 5, count: 5 },
  ];

  // Define Advanced Mode progress rows (wired to actual data)
  const advancedModeRows = [
    {
      label: 'Correct',
      color: 'tw-bg-blue-500',
      glow: '#3b82f6',
      maxCount: 5,
      count: progress.advancedModeQuestionsCorrect,
    },
    {
      label: 'First-Try',
      color: 'tw-bg-green-500',
      glow: '#22c55e',
      maxCount: 5,
      count: progress.advancedModeFirstTryCorrect,
    },
    {
      label: 'Streak',
      color: 'tw-bg-purple-500',
      glow: '#a855f7',
      maxCount: 5,
      count: progress.advancedModeCurrentStreak,
    },
  ];

  const isAdvancedModeDotsComplete = advancedModeRows.every(row => row.count >= row.maxCount);
  const isPracticeSectionComplete = isAdvancedModeDotsComplete;
  const latestChallengeScore = attemptInformation?.totalScore ?? skill213State?.skillAssessment?.score ?? null;
  const isChallengeSectionComplete = (latestChallengeScore ?? -1) >= CHALLENGE_PASSING_SCORE;
  const isExceedComplete = isAllPerfectFound(progress.exceedRiceGamePoints ?? []);

  const handleMoveForward = useCallback(() => {
    if (isChallengeSectionComplete) {
      setCurrentPage('exceed');
    } else {
      navigate('/');
    }
  }, [isChallengeSectionComplete, navigate, setCurrentPage]);

  // Define achievement sections
  // IMPORTANT: Badge data is now pulled from achievementMetadata.ts - the single source of truth
  // Section structure is defined here, but icons, titles, and descriptions come from metadata
  const badgeSections = [
    {
      title: 'Simple Ratios',
      achievementIds: ['double', '3-to-8', '8-to-3'],
    },
    {
      title: 'Complex Ratios',
      achievementIds: ['bikes', 'multi-bike', 'sequenced', 'equivalent-ratios', 'spiders', 'part-to-whole'],
    },
  ].map(section => ({
    title: section.title,
    badges: section.achievementIds.map(id => {
      const metadata = getAchievementMetadata(id);
      if (!metadata) {
        console.error(`Achievement metadata not found for ID: ${id}`);
        return {
          icon: '❓',
          title: 'Unknown',
          description: 'Unknown achievement',
          state: 'empty' as const,
        };
      }
      return {
        icon: metadata.icon,
        title: metadata.title,
        description: metadata.shortDescription,
        state: (progress.achievements[id] || 'empty') as 'empty' | 'bronze' | 'silver' | 'gold',
      };
    }),
  }));

  const isDotsComplete = true; // Always complete for this demo
  const allBadges = badgeSections.flatMap(section => section.badges);

  // Development validation: Check that badge order matches ACHIEVEMENT_DISPLAY_ORDER
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const badgeIds = allBadges.map((_, index) => ACHIEVEMENT_DISPLAY_ORDER[index]);
      // Check if we can find a mismatch
      const hasOrderMismatch = badgeIds.some((expectedId, index) => {
        const badge = allBadges[index];
        // Try to match by checking if the state comes from this achievement
        const stateValue = progress.achievements[expectedId];
        return badge.state !== stateValue && stateValue !== undefined;
      });

      if (hasOrderMismatch) {
        console.warn(
          '[App.tsx] Badge order may not match ACHIEVEMENT_DISPLAY_ORDER in achievementMetadata.ts',
          '\nExpected order:',
          ACHIEVEMENT_DISPLAY_ORDER,
          '\nVerify badgeSections array matches this order',
        );
      }
    }
  }, []); // Only run once on mount

  // Apply dark mode when advanced mode is active
  useEffect(() => {
    if (isPracticePage) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, [isPracticePage]);

  useEffect(() => {
    if (progress.currentPage !== 'connect') {
      return;
    }

    const activeSlide = CONNECT_SLIDES[connectCurrentSlideIndex];
    if (!activeSlide || connectViewedSlideIds.includes(activeSlide.id)) {
      return;
    }

    updateProgress({
      connectViewedSlideIds: [...connectViewedSlideIds, activeSlide.id],
    });
  }, [connectCurrentSlideIndex, connectViewedSlideIds, progress.currentPage, updateProgress]);

  // OLD: renderLockedSectionPlaceholder was used for the locked Exceed section.
  // const renderLockedSectionPlaceholder = (sectionName: string): React.ReactNode => (
  //   <div
  //     style={{
  //       padding: 'var(--spacing-3)',
  //       color: 'var(--muted-foreground)',
  //       fontSize: '12px',
  //       lineHeight: 1.4,
  //     }}
  //   >
  //     {sectionName} is coming soon.
  //   </div>
  // );

  const handleRetakeChallenge = useCallback(() => {
    refetchLrnSession();
    setAttemptInformation(null);
  }, [refetchLrnSession]);

  const handleCurrentStatus = (_value: { classname: string; status: string | undefined } | null) => undefined;

  // const handleSetTimelineResult = useCallback(
  //   (item: SkillResourceSteps) => {
  //     if (item === SkillResourceSteps.WORKED_EXAMPLE) {
  //       setCurrentPage('connect');
  //     } else if (item === SkillResourceSteps.PRACTICE_PROBLEMS) {
  //       activateAdvancedMode();
  //     }
  //   },
  //   [activateAdvancedMode, setCurrentPage],
  // );

  // const handleResetTimelineOnNavigation = () => undefined;

  const sidebarSections: SectionSidebarItem[] = [
    {
      id: 'explore',
      label: 'Explore',
      innerNav: (
        <ProblemTreePanel
          embedded
          rows={rows}
          badgeSections={badgeSections}
          isDotsComplete={isDotsComplete}
          isBadgesComplete={advancedModeUnlocked}
          onAdvancedModeClick={handleAdvancedModeClick}
          selectedGoal={progress.selectedGoal}
          onGoalSelect={setSelectedGoal}
          unlockingBadges={unlockingBadges}
          onBadgeUnlockComplete={onBadgeUnlockComplete}
        />
      ),
      completed: isExploreSectionComplete,
    },
    {
      id: 'connect',
      label: 'Connect',
      innerNav: (
        <ConnectInnerNav
          currentSlideIndex={connectCurrentSlideIndex}
          viewedSlideIds={connectViewedSlideIds}
          onSelectSlide={handleConnectSlideSelect}
        />
      ),
      disabled: false,
      locked: false,
      completed: isConnectSectionComplete,
    },
    {
      id: 'practice',
      label: 'Practice',
      innerNav: (
        <AdvancedModeProgressPanel
          embedded
          rows={advancedModeRows}
          isDotsComplete={isAdvancedModeDotsComplete}
          onSkillChallengeClick={handleSkillChallengeClick}
        />
      ),
      disabled: !isPracticeMenuUnlocked,
      locked: !isPracticeMenuUnlocked,
      completed: isPracticeSectionComplete,
      supportText: !isPracticeMenuUnlocked ? 'Complete Explore or Connect.' : undefined,
    },
    {
      id: 'challenge',
      label: 'Challenge',
      innerNav: (
        <ChallengeInnerNav
          previousAttempts={previousAttempts}
          attemptInformation={attemptInformation}
          latestChallengeScore={latestChallengeScore}
          onRetakeChallenge={handleRetakeChallenge}
        />
      ),
      disabled: false,
      locked: false,
      completed: isChallengeSectionComplete,
    },
    {
      id: 'exceed',
      label: 'Exceed',
      supportText: isChallengeSectionComplete ? "You've unlocked a mini-game!" : 'Pass the Challenge to unlock.',
      innerNav: isChallengeSectionComplete ? (
        <div className="tw-p-2 tw-flex tw-flex-col tw-gap-3 tw-text-sm tw-text-neutral-700">
          <p className="tw-m-0">You&apos;ve earned this! Play the game or move on if you feel like it!</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="tw-font-atkinson tw-text-sm tw-font-semibold tw-bg-transparent tw-border tw-rounded-lg tw-cursor-pointer tw-px-3 tw-py-2 tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors"
            style={{ borderColor: 'var(--border)' }}
          >
            Choose a Different Skill
          </button>
        </div>
      ) : (
        <div
          style={{
            padding: 'var(--spacing-3)',
            color: 'var(--muted-foreground)',
            fontSize: '12px',
            lineHeight: 1.4,
          }}
        >
          Pass the Skill Challenge to unlock a bonus mini-game.
        </div>
      ),
      disabled: !isChallengeSectionComplete,
      locked: !isChallengeSectionComplete,
      completed: isExceedComplete,
    },
  ];

  const sidebarWidth = `clamp(${TEAME_183_SIDEBAR_MIN_WIDTH_PX}px, ${TEAME_183_SIDEBAR_WIDTH_PERCENT}%, ${TEAME_183_SIDEBAR_MAX_WIDTH_PX}px)`;

  return (
    <div
      className="tw-w-full tw-h-full tw-min-h-0 tw-flex tw-flex-col tw-relative tw-overflow-visible"
      style={{
        backgroundColor: isPracticePage ? '#1e1e1e' : '#f9f8f5',
        color: isPracticePage ? '#f9f8f5' : '#393939',
      }}
    >
      {/* <ResetButton /> */}
      <div className="tw-mx-2 tw-grid tw-min-h-[42px] tw-grid-cols-[1fr_auto_1fr] tw-items-end tw-gap-2 tw-shrink-0 md:tw-mx-3">
        <div className="tw-justify-self-start">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="tw-flex tw-items-center tw-gap-2 tw-text-blue-500 tw-font-atkinson tw-text-base tw-font-bold tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-text-blue-400"
          >
            <ArrowLeft className="tw-h-4 tw-rounded-full tw-inline-flex tw-justify-center tw-items-center tw-gap-2 tw-overflow-hidden" />
            Back to skill selection
          </button>
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
          <h1
            className="tw-m-0 tw-font-atkinson tw-text-2xl md:tw-text-3xl tw-font-bold"
            style={{ color: isPracticePage ? '#f9f8f5' : '#393939' }}
          >
            {skillName}
          </h1>
          <BetaLabel
            rating={progress.betaFeedbackRating}
            feedbackText={progress.betaFeedbackText}
            onRatingChange={handleBetaRatingChange}
            onFeedbackTextChange={handleBetaFeedbackTextChange}
          />
        </div>
        <div />
      </div>
      <div
        className={`tw-flex tw-flex-1 tw-min-h-0 ${
          isChallengePage ? 'tw-overflow-x-auto tw-overflow-y-hidden' : 'tw-overflow-auto'
        }`}
        style={{
          padding: 'var(--spacing-2, 8px) 0',
          minWidth: 0,
        }}
      >
        <div className="tw-mx-2 tw-flex tw-min-h-0 tw-flex-1 tw-gap-2 md:tw-mx-3" style={{ minWidth: 0 }}>
          <SectionSidebar
            sections={sidebarSections}
            selectedSectionId={progress.currentPage}
            onSectionSelect={id => handleSidebarSectionSelect(id as LearningPage)}
            width={sidebarWidth}
          />
          <div
            className="tw-relative tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-basis-0 tw-flex-col"
            style={{
              minWidth: `${TEAME_183_MAIN_VIEWPORT_MIN_WIDTH_PX}px`,
              minHeight: isChallengePage ? 0 : `${TEAME_183_MAIN_VIEWPORT_MIN_HEIGHT_PX}px`,
            }}
          >
            {isPracticePage ? (
              <AdvancedModeRatioNew key={progress.resetKey} />
            ) : progress.currentPage === 'connect' ? (
              <ConnectSlides
                currentSlideIndex={connectCurrentSlideIndex}
                onSelectSlide={handleConnectSlideSelect}
                onComplete={handleConnectComplete}
                completionTargetLabel={isPracticeMenuUnlocked ? 'Practice' : 'Explore'}
              />
            ) : progress.currentPage === 'exceed' ? (
              <RiceCookingGame />
            ) : progress.currentPage === 'challenge' ? (
              lrnSessionData?.sessionId ? (
                <FullSizeViewport>
                  <AssessmentComponent
                    key={lrnSessionData.sessionId}
                    workedExampleUrl={null}
                    title={skillName}
                    setAttemptInformation={setAttemptInformation}
                    attemptInformation={attemptInformation}
                    setAssessmentState={setAssessmentState}
                    setPreviousAttempts={setPreviousAttempts}
                    previousAttempts={previousAttempts}
                    refetchLrnSession={refetchLrnSession}
                    setCurrentStatus={handleCurrentStatus}
                    lrnSessionId={lrnSessionData}
                    essentialSkills={[]}
                    sssAfterTakeAssessment={sssAfterTakeAssessment}
                    onMoveForward={handleMoveForward}
                    moveForwardLabel={isChallengeSectionComplete ? 'Continue to Exceed' : 'Move Forward'}
                    skipNewActivityId={false}
                    setSkipNewActivityId={() => undefined}
                    setPreviousStatus={() => undefined}
                    setDisabledReviewModal={() => undefined}
                    setCurrentStep={() => undefined}
                    setAssessmentInProgress={() => {}}
                    assessmentInProgress={false}
                    // Standalone experiment harness: no roadmap skill state to read prior credit from.
                    preAttemptSubstateId={null}
                  />
                </FullSizeViewport>
              ) : (
                <FullSizeViewport>
                  <div className="tw-w-full tw-rounded-md tw-border tw-border-neutral-200 tw-bg-white tw-p-4 tw-text-sm tw-text-neutral-700">
                    Preparing your challenge session...
                  </div>
                </FullSizeViewport>
              )
            ) : (
              <>
                <BlockBuilder
                  key={progress.manipulativeResetKey}
                  achievementId={progress.selectedGoal}
                  currentLines={progress.manipulativeState.lines}
                  currentArrangement={progress.manipulativeState.arrangement}
                  currentMultiplier={progress.manipulativeState.cloneMultiplier}
                />
                {/* Reset Diagram button - bottom right of workspace */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleResetDiagram}
                      className="tw-absolute tw-bg-white tw-text-neutral-500 hover:tw-text-neutral-950 tw-transition-colors tw-flex tw-items-center tw-cursor-pointer"
                      style={{
                        bottom: 'var(--spacing-4, 16px)',
                        right: 'var(--spacing-4, 16px)',
                        padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)',
                        boxShadow: 'var(--elevation-sm)',
                        gap: 'var(--spacing-2, 8px)',
                        fontFamily: 'var(--font-atkinson)',
                        fontSize: '14px',
                        fontWeight: 'var(--font-weight-medium)',
                        zIndex: 10,
                      }}
                    >
                      <RotateCcw className="tw-w-4 tw-h-4" />
                      Reset Diagram
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="tw-text-white">Reset to initial configuration</span>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <MathEducationProvider>
      <TooltipProvider>
        <AppContent />
      </TooltipProvider>
    </MathEducationProvider>
  );
}
